import React from 'react';

const FIELD_MAP = [
  { key: 'bank_name', label: 'Bank Name', sensitive: false },
  { key: 'customer_name', label: 'Customer Name', sensitive: true },
  { key: 'city', label: 'City', sensitive: false },
  { key: 'settlement_type', label: 'Type of Settlement', sensitive: false },
  { key: 'account_loan_number', label: 'Account/Loan Number', sensitive: true },
  { key: 'account_type', label: 'Account Type', sensitive: false },
  { key: 'original_outstanding_amount', label: 'Original Outstanding', sensitive: true },
  { key: 'settlement_amount', label: 'Settlement Amount', sensitive: true },
  { key: 'waiver_amount', label: 'Waiver Amount', sensitive: true },
  { key: 'number_of_installments', label: 'Installments', sensitive: false },
  { key: 'installment_due_dates', label: 'Installment Due Date(s)', sensitive: false },
  { key: 'settlement_letter_date', label: 'Settlement Letter Date', sensitive: false },
  { key: 'settlement_expiry_date', label: 'Settlement Expiry Date', sensitive: false },
  { key: 'settlement_reference_number', label: 'Settlement Reference', sensitive: true },
  { key: 'credit_bureau_status_post_settlement', label: 'Credit Bureau Status', sensitive: false },
  { key: 'noc_conditions', label: 'NOC Conditions', sensitive: false },
  { key: 'legal_withdrawal_requirement', label: 'Legal Withdrawal', sensitive: false },
  { key: 'payment_mode', label: 'Payment Mode', sensitive: false },
  { key: 'outstanding_amount_as_on_date', label: 'Outstanding Amount As-On', sensitive: true },
  { key: 'upi_id', label: 'UPI ID', sensitive: true },
  { key: 'negotiator_bank_rep_name_and_id', label: 'Bank Rep / Employee ID', sensitive: true },
  { key: 'card_number_masked', label: 'Card Number (Masked)', sensitive: true },
  { key: 'alternate_account_number', label: 'Alternate Account (AAN)', sensitive: true },
  { key: 'outstanding_balance_as_on_date', label: 'Outstanding Balance As-On', sensitive: true },
];

const styles = {
  section: {
    marginBottom: 28,
  },
  sectionLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    color: 'var(--text-tertiary)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  field: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    padding: '12px 14px',
    transition: 'border-color 0.15s',
  },
  fieldLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    color: 'var(--text-tertiary)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  fieldValue: {
    fontFamily: 'var(--font-mono)',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  fieldValueMasked: {
    fontFamily: 'var(--font-mono)',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-tertiary)',
    letterSpacing: '4px',
  },
  fieldNull: {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    color: 'var(--text-tertiary)',
    fontStyle: 'italic',
  },
  sensitiveTag: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: 9,
    letterSpacing: '0.06em',
    background: 'var(--danger-bg)',
    color: 'var(--danger)',
    marginLeft: 6,
    textTransform: 'uppercase',
    verticalAlign: 'middle',
  },
  summaryBox: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    padding: '16px 18px',
    fontFamily: 'var(--font-mono)',
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.7,
    whiteSpace: 'pre-wrap',
    borderLeft: '3px solid var(--accent)',
  },
  maskToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    cursor: 'pointer',
    userSelect: 'none',
  },
  toggle: {
    width: 32,
    height: 18,
    borderRadius: 9,
    position: 'relative',
    transition: 'background 0.2s',
    flexShrink: 0,
  },
  toggleThumb: {
    position: 'absolute',
    top: 3,
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: 'var(--toggle-knob)',
    boxShadow: '0 1px 2px var(--shadow)',
    transition: 'left 0.2s',
  },
  toggleLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
};

export default function ExtractedData({ data, summary, masked, onMaskToggle, showMaskToggle }) {
  const effectiveMasked = showMaskToggle && masked;

  const visibleFields = FIELD_MAP.filter(({ key }) => {
    const val = data?.[key];
    return val != null && String(val).trim() !== '';
  });

  return (
    <div>
      {summary ? (
        <div style={styles.section}>
          <div style={styles.sectionLabel}>summary</div>
          <div style={styles.summaryBox}>{summary}</div>
        </div>
      ) : null}

      <div style={styles.section}>
        <div style={styles.sectionLabel}>extracted fields</div>

        {showMaskToggle ? (
          <div
            style={styles.maskToggle}
            onClick={onMaskToggle}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onMaskToggle()}
          >
            <div style={{
              ...styles.toggle,
              background: masked ? 'var(--accent)' : 'var(--border)',
            }}>
              <div style={{
                ...styles.toggleThumb,
                left: masked ? 17 : 3,
              }} />
            </div>
            <span style={styles.toggleLabel}>
              {masked ? 'Sensitive data masked' : 'Mask sensitive data'}
            </span>
          </div>
        ) : null}

        {visibleFields.length > 0 ? (
          <div style={styles.grid}>
            {visibleFields.map(({ key, label, sensitive }) => {
              const val = data[key];
              const shouldMask = effectiveMasked && sensitive;
              return (
                <div key={key} style={styles.field}>
                  <div style={styles.fieldLabel}>
                    {label}
                    {sensitive && <span style={styles.sensitiveTag}>sensitive</span>}
                  </div>
                  <div style={shouldMask ? styles.fieldValueMasked : styles.fieldValue}>
                    {shouldMask ? '••••••' : val}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={styles.fieldNull}>No fields extracted from this document.</div>
        )}
      </div>
    </div>
  );
}
